import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  getTransactions,
  ITransactionFilters,
  ITransactionItem,
} from "../utils/services/modules/transactions";

const PAGE_SIZE = 20;

// Histórico paginado com filtros. Sempre que os filtros mudam a lista volta
// para a página 1 e a requisição anterior é cancelada, para que uma resposta
// atrasada de um filtro antigo não sobrescreva a lista atual.
// `filters` precisa ser memorizado (useMemo) por quem chama.
export function useTransactions(
  token: string | undefined,
  filters: ITransactionFilters,
) {
  const [items, setItems] = useState<ITransactionItem[]>([]);
  const [page, setPage] = useState(0); // última página carregada
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (pageToLoad: number) => {
      if (!token) return;

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      setLoading(true);
      setError(false);

      try {
        const data = await getTransactions(
          token,
          filters,
          pageToLoad,
          PAGE_SIZE,
          controller.signal,
        );
        setItems((prev) =>
          pageToLoad === 1
            ? data.transactions
            : [...prev, ...data.transactions],
        );
        setPage(data.page);
        setTotal(data.total);
        setHasMore(data.page < data.totalPages);
      } catch (err) {
        if (axios.isCancel(err)) return; // substituída por uma requisição nova
        console.error("Erro ao buscar transações:", err);
        setError(true);
      } finally {
        if (controllerRef.current === controller) setLoading(false);
      }
    },
    [token, filters],
  );

  useEffect(() => {
    setItems([]);
    setPage(0);
    setTotal(0);
    setHasMore(true);
    load(1);

    return () => controllerRef.current?.abort();
  }, [load]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && !error) load(page + 1);
  }, [loading, hasMore, error, load, page]);

  const retry = useCallback(() => load(page + 1), [load, page]);

  return {
    items,
    total,
    hasMore,
    loading,
    error,
    initialLoading: loading && page === 0,
    loadMore,
    retry,
  };
}
